import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Dialog, DialogContent, DialogPortal } from "@/components/ui/dialog";
import {
  IconX,
  IconTrophy,
  IconTarget,
  IconFishHook,
  IconBrandTiktok,
  IconBrandMeta,
  IconCarambola,
  IconHandFinger,
  IconMovie,
  IconPasswordUser,
  IconPokerChip,
  IconBrandGoogle,
  IconLoader2,
} from "@tabler/icons-react";
import {
  Column,
  CreativeOverviewRow,
  CreativeReportColumn,
  CreativeReportRow,
  MetricData,
  Platform,
  CreativeOverviewItem,
} from "@/screens/creatives/types";
import { ColumnOption } from "@/types";
import AdCard from "@/components/common/adCard";
import MetricCard from "../cards/MetricCard";
import { useGlobalStore } from "@/GlobalStore";
import { Button } from "@/components/ui/button";
import ATRAction from "../card-actions/ATRAction";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  getOverviewIcon,
  getPlatformIcon,
  getMetricFormulaLabel,
} from "@/screens/utils/Helpers";
import CreativeTable from "../../table/CreativeTable";
import { flexRender } from "@tanstack/react-table";
import { CreativeReportMetadata } from "@/types";
import EditMetricsToggle from "@/components/common/metrics/components/EditMetricsToggle";
import Metrics from "@/components/common/metrics";
import { fetchCreativeOverviews } from "@/screens/creatives/apiService";
import axios, { CancelTokenSource } from "axios";
import WaveLoader from "@/components/common/loaders/WaveLoader";

interface CreativeDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  creative?: CreativeOverviewRow | null;
  columnData?: Column[];
  defaultMetricId?: string;
  metricColumns?: Column[];
  reportMetadata?: CreativeReportMetadata;
}

const CreativeDetailsDialog: React.FC<CreativeDetailsDialogProps> = ({
  open,
  onOpenChange,
  creative,
  columnData,
  defaultMetricId,
  metricColumns,
  reportMetadata,
}) => {
  const { selectedAccount, dataConfigurations } = useGlobalStore();
  const [showBottomGradient, setShowBottomGradient] = useState(false);
  const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(null);
  const [selectedMetric, setSelectedMetric] = useState<string | undefined>(
    defaultMetricId,
  );
  const [overviewsData, setOverviewsData] = useState<CreativeOverviewItem[]>([]);
  const [overviewsLoading, setOverviewsLoading] = useState(false);

  // LRU cache for overviews keyed by creative_id (max 10 entries per dialog instance / section)
  const overviewsCacheRef = useRef(new Map<string, CreativeOverviewItem[]>());

  const metricOptions = useMemo(
    () =>
      dataConfigurations?.column_choices
        ?.find((c) => c.key === "metric")
        ?.options?.filter((o) => metricColumns?.map((m) => m.key).includes(o.key)) ?? [],
    [dataConfigurations, metricColumns],
  );

  const defaultMetricsKeys = dataConfigurations?.column_choices.find(
    (c) => c.key === "metric",
  )?.default;

  const defaultMetricsOptions = useMemo(() => {
    if (!defaultMetricsKeys || !metricOptions) return [];

    // Return the metric options in the same order as defaultMetricsKeys
    return defaultMetricsKeys
      .map((key) => metricOptions.find((option) => option.key === key))
      .filter((option): option is NonNullable<typeof option> => option !== undefined);
  }, [defaultMetricsKeys, metricOptions]);

  const handleMetricChange = (metric: string) => {
    setSelectedMetric(metric);
  };

  const getMetricCardsData = (): MetricData[] => {
    if (!columnData || !creative) return [];

    const startDate = creative.ads?.[0]?.creative?.start_date;

    return columnData
      .map((column): MetricData => {
        const valueKey = column.is_kpi ? selectedMetric || "" : column.key;
        const metricColumn = column.is_kpi
          ? (metricOptions?.find((col) => col.key === valueKey) ?? column)
          : column;
        const changeKey = `${valueKey}_percent_change`;
        const value = creative[
          valueKey as keyof CreativeOverviewRow
        ] as MetricData["value"];
        const percentChange = creative[
          changeKey as keyof CreativeOverviewRow
        ] as MetricData["percentChange"];

        return {
          key: valueKey,
          label: metricColumn.label,
          value: value,
          prefix: metricColumn.prefix ?? "",
          suffix: metricColumn.suffix ?? "",
          percentChange: percentChange,
          hasDropdown: column.is_kpi || false,
        };
      })
      .concat({
        key: "launch_date",
        label: "Launch Date",
        value: startDate
          ? new Date(startDate).toLocaleDateString("en-GB", {
            day: "numeric",
            month: "long",
            year: "numeric",
          })
          : "N/A",
        prefix: "",
        suffix: "",
        hasDropdown: false,
      });
  };

  // Check scroll state
  const checkScrollState = useCallback(() => {
    if (!scrollContainer) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
    const isScrollable = scrollHeight > clientHeight;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px threshold
    setShowBottomGradient(isScrollable && !isAtBottom);
  }, [scrollContainer]);

  // Callback ref to capture the scroll container when it's mounted
  const scrollContainerRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      setScrollContainer(node);
    }
  }, []);

  useEffect(() => {
    setSelectedMetric(defaultMetricId);
  }, [defaultMetricId]);

  // Fetch overviews from API when dialog opens, with LRU cache (10 entries)
  useEffect(() => {
    if (!open || !creative || !selectedAccount?.id) {
      setOverviewsData([]);
      return;
    }

    const creativeId = creative.ads?.[0]?.creative_id;
    if (!creativeId) {
      setOverviewsData([]);
      return;
    }

    const cache = overviewsCacheRef.current;

    // Check cache first
    const cached = cache.get(creativeId);
    if (cached) {
      // Move to end (most recently used) by re-inserting
      cache.delete(creativeId);
      cache.set(creativeId, cached);
      setOverviewsData(cached);
      setOverviewsLoading(false);
      return;
    }

    let cancelTokenSource: CancelTokenSource | null = null;

    const fetchOverviews = async () => {
      try {
        setOverviewsLoading(true);
        cancelTokenSource = axios.CancelToken.source();
        const response = await fetchCreativeOverviews(
          selectedAccount.id,
          creativeId,
          undefined, // promptIds - optional, undefined means default prompts
          cancelTokenSource.token,
        );
        const data = response.data || [];
        setOverviewsData(data);

        // Store in cache and evict oldest if over limit
        cache.set(creativeId, data);
        if (cache.size > 10) {
          const oldestKey = cache.keys().next().value;
          if (oldestKey !== undefined) cache.delete(oldestKey);
        }
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error("Error fetching creative overviews:", error);
          setOverviewsData([]);
        }
      } finally {
        setOverviewsLoading(false);
      }
    };

    void fetchOverviews();

    return () => {
      if (cancelTokenSource) {
        cancelTokenSource.cancel("Component unmounted or creative changed");
      }
    };
  }, [open, creative, selectedAccount?.id]);

  // Set up scroll listener when container is available
  useEffect(() => {
    if (!scrollContainer || !creative || !open) return;

    // Initial checks with different delays to catch content loading
    const timeouts = [
      setTimeout(checkScrollState, 0),
      setTimeout(checkScrollState, 100),
      setTimeout(checkScrollState, 300),
      setTimeout(checkScrollState, 500),
    ];

    scrollContainer.addEventListener("scroll", checkScrollState);
    scrollContainer.addEventListener("resize", checkScrollState);

    return () => {
      timeouts.forEach(clearTimeout);
      scrollContainer.removeEventListener("scroll", checkScrollState);
      scrollContainer.removeEventListener("resize", checkScrollState);
    };
  }, [scrollContainer, checkScrollState, creative, open]);

  const accountContributionData = creative?.account_contributions;
  const totalAccountContribution = creative; // using total data directly from parent data

  const [showAddMetric, setShowAddMetric] = useState<boolean>(false);

  const [selectedTableMetrics, setSelectedTableMetrics] = useState<ColumnOption[]>([]);

  useEffect(() => {
    setSelectedTableMetrics(defaultMetricsOptions);
  }, [defaultMetricsOptions]);

  const handleTableMetricChange = (metrics: ColumnOption[]) => {
    setSelectedTableMetrics(metrics);
  };

  // Memoize the columns array to prevent infinite re-renders
  // Transform ColumnOption to CreativeReportColumn by looking up column_type from columnData
  const tableColumns = useMemo(
    () =>
      [
        {
          key: "account",
          label: "Ad Account",
          is_primary: true,
          column_type: "text",
          prefix: "{PLATFORM_ICON}",
          suffix: "",
        },
        ...selectedTableMetrics.map((metric) => {
          const originalColumn = columnData?.find((col) => col.key === metric.key);
          return {
            key: metric.key,
            label: metric.label,
            column_type: originalColumn?.type || originalColumn?.column_type || "number",
            prefix: metric.prefix ?? "",
            suffix: metric.suffix ?? "",
            display_formula: getMetricFormulaLabel(
              metric.metric_formula ?? null,
              metricOptions,
            ),
          };
        }),
      ] as CreativeReportColumn[],
    [selectedTableMetrics, columnData, metricOptions],
  );

  // Filter and order API overviews
  const orderedOverviews = useMemo(() => {
    const filtered = overviewsData.filter((item) => item.sync_status !== "failed"); // Hide failed overviews
    return [
      ...filtered.filter((item) => item.overview_prompt.is_default),
      ...filtered.filter((item) => !item.overview_prompt.is_default),
    ];
  }, [overviewsData]);

  if (!creative) return null;

  const firstAd = creative.ads?.[0] ?? creative.first_ad_metadata;
  const creativeData = firstAd?.creative;
  const creativeType =
    creativeData?.videos?.length &&
      creativeData?.videos?.length > 0 &&
      creativeData?.videos?.[0]?.permalink_url
      ? "video"
      : "image";

  const achievements = creative.achievements || [];

  const metricColumnData: MetricData[] = getMetricCardsData();

  return (
    <>
      {/* Custom overlay (default overlay was blocking cursor event on dropdowns) */}

      <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
        <DialogPortal>
          {open && (
            <div
              className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
              onClick={() => onOpenChange(false)}
            />
          )}
          <DialogContent
            hideCloseButton
            hideOverlay
            className="p-2 max-w-5xl max-h-[80vh] flex flex-col gap-[19px] overflow-hidden squircle-mask squircle-radius-[16px] bg-white border-none"
          >
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-[32px] h-[32px] bg-selected-gray/80 rounded-[8px] flex items-center justify-center">
                  {selectedAccount?.platform.toLowerCase() === "facebook" ? (
                    <IconBrandMeta size={22} className="text-blue-500" />
                  ) : selectedAccount?.platform.toLowerCase() === "tiktok" ? (
                    <IconBrandTiktok size={22} className="text-black" />
                  ) : selectedAccount?.platform.toLowerCase() === "google" ? (
                    <IconBrandGoogle size={22} className="text-black" />
                  ) : null}
                </div>
                <div>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <h2 className="text-[14px] text-brand-primary-new max-w-[300px] truncate">
                        {creative.creative_name || "Creative Name"}
                      </h2>
                    </TooltipTrigger>
                    <TooltipContent
                      side="bottom"
                      align="start"
                      className="max-w-[400px] text-wrap"
                    >
                      {creative.creative_name || "Creative Name"}
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
              <Button
                variant="sq-outline"
                onClick={() => onOpenChange(false)}
                className="gap-[6px]"
              >
                <IconX size={14} className="text-brand-primary-400" />
                <span className="mt-[2px]">Close</span>
              </Button>
            </div>

            <div className="flex-1 flex gap-2 overflow-hidden">
              {/* Left Panel - Creative Details */}
              <div className="w-[266px] shrink-0">
                {/* Creative Media */}
                {creativeData && (
                  <div className="relative">
                    <AdCard
                      header={{
                        title: creativeData.account_name || "Unknown Brand",
                        icon: creativeData.account_icon_url,
                      }}
                      body={{
                        description: creativeData.bodies?.[0] ?? "",
                        media: {
                          ad_id: firstAd?.ad_id || "",
                          type: creativeType,
                          source:
                            creativeType === "image"
                              ? creativeData.images?.[0]?.permalink_url
                              : creativeData.videos?.[0]?.permalink_url,
                          thumbnail: creativeData.thumbnail_url,
                          actions: [
                            {
                              component: (
                                <ATRAction
                                  rank={
                                    creative.all_time_rank || creative.current_rank || 0
                                  }
                                />
                              ),
                              position: "top-left",
                              visibility: "always",
                            },
                          ],
                        },
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Right Panel - Performance & Content Details */}
              <div className="flex-1 flex flex-col relative overflow-hidden">
                <div
                  ref={scrollContainerRef}
                  className="flex flex-col gap-[8px] overflow-y-auto"
                >
                  {/* Performance Metrics */}

                  <div className="w-full p-[1px] flex rounded-[12px] squircle squircle-radius-[12px] squircle-outline-[1px] squircle-outline-color-border-gray">
                    {metricColumnData?.slice(0, 3).map((metric) => (
                      <MetricCard
                        key={metric.key}
                        title={metric.hasDropdown ? undefined : metric.label}
                        value={metric.value}
                        prefix={metric.prefix}
                        suffix={metric.suffix}
                        change={metric.percentChange}
                        className="flex-1 min-w-0 max-w-1/3"
                        metricOptions={metricOptions}
                        hasDropdown={metric.hasDropdown}
                        selectedMetric={metric.hasDropdown ? selectedMetric : undefined}
                        onMetricChange={
                          metric.hasDropdown ? handleMetricChange : undefined
                        }
                      />
                    ))}
                  </div>

                  {/* Achievements */}
                  <OverviewCard title="Achievements" titleIcon={IconCarambola}>
                    <div className="flex flex-wrap gap-3">
                      {achievements.map((achievement, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-[6px] h-[32px] px-[12px] py-[6px] rounded-[8px] text-xs"
                          style={{
                            backgroundColor: getAchievementColor(achievement.label),
                            color: getAchievementTextColor(achievement.label),
                          }}
                        >
                          {getAchievementIcon(achievement.label)}
                          <span>{achievement.label}</span>
                          <span className="font-medium">#{achievement.value}</span>
                        </div>
                      ))}
                      {/* Fallback achievements if none exist */}
                      {achievements.length === 0 && (
                        <span className="text-[11px] text-brand-primary-400">
                          No Achievements yet
                        </span>
                      )}
                    </div>
                  </OverviewCard>
                  {accountContributionData && accountContributionData.length > 0 && (
                    <OverviewCard
                      key={"account_breakdown"}
                      title="Account Overview"
                      titleIcon={IconPokerChip}
                      rightSideHeader={
                        <EditMetricsToggle
                          active={showAddMetric}
                          onToggle={() => setShowAddMetric((i) => !i)}
                        />
                      }
                    >
                      {showAddMetric && (
                        <Metrics
                          metricsOptions={metricOptions}
                          selectedMetrics={selectedTableMetrics}
                          onMetricChange={handleTableMetricChange}
                          className="mb-2"
                        />
                      )}

                      <CreativeTable
                        reportMetadata={reportMetadata}
                        data={accountContributionData as CreativeReportRow[]}
                        columns={tableColumns}
                        totalRow={
                          accountContributionData.length > 1
                            ? (totalAccountContribution as CreativeReportRow)
                            : undefined
                        }
                        showPaginationIfPagination={true}
                        paginationTitle="Accounts per page"
                        metricOptions={metricOptions}
                        selectedMetrics={selectedTableMetrics}
                        onMetricChange={handleTableMetricChange}
                        showAddMetricButton={true}
                        overwritePrimaryCell={(cell) => {
                          // Get the raw cell value
                          const cellValue = cell.getValue();
                          const cellText = String(cellValue);

                          const accountPlatform = selectedAccount?.child_accounts?.find(
                            (a) => a.name.trim() === cellText.trim(),
                          )?.platform as unknown as Platform;

                          // Also get the rendered cell component
                          const cellComponent = flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          );

                          return (
                            <div className="flex gap-2 items-center px-1 min-h-[43px]">
                              {accountPlatform && (
                                <img
                                  className="h-[14px] w-[14px] flex-shrink-0 -translate-y-[1px]"
                                  src={getPlatformIcon(accountPlatform)}
                                />
                              )}

                              <div className="text-[12px] font-medium text-brand-primary-new/80 group-hover/row:text-brand-primary-new">
                                {cellComponent}
                              </div>
                            </div>
                          );
                        }}
                      />
                    </OverviewCard>
                  )}

                  {/* Display overview data */}
                  {overviewsLoading && orderedOverviews.length === 0 && (
                    <div className="flex items-center justify-center py-4">
                      <WaveLoader className="w-[100px]" />
                    </div>
                  )}
                  {orderedOverviews?.map((overviewItem) => {
                    const overviewTitle = overviewItem.overview_prompt.title || "Overview";
                    const overviewIcon = getOverviewIcon(
                      overviewTitle,
                      overviewItem.overview_prompt.is_default,
                    );
                    const isPending = overviewItem.sync_status === "pending";

                    return (
                      <OverviewCard
                        key={overviewItem.id}
                        title={overviewTitle}
                        titleIcon={overviewIcon}
                      >
                        {isPending ? (
                          <div className="flex items-center gap-2 text-[12px] text-brand-primary-400">
                            <IconLoader2 size={14} className="animate-spin" />
                            <span>Generating overview...</span>
                          </div>
                        ) : overviewItem.content ? (
                          <p className="text-[12px] whitespace-pre-wrap text-brand-primary-800 leading-relaxed">
                            {overviewItem.content}
                          </p>
                        ) : (
                          <p className="text-[12px] text-brand-primary-400">
                            No content available
                          </p>
                        )}
                      </OverviewCard>
                    );
                  })}
                  {!overviewsLoading && orderedOverviews.length === 0 && (
                    <OverviewCard title="Overviews" titleIcon={IconTarget}>
                      <p className="text-[12px] text-brand-primary-400">
                        No overviews available
                      </p>
                    </OverviewCard>
                  )}
                </div>

                {/* Bottom gradient overlay */}
                {showBottomGradient && (
                  <div
                    className="absolute bottom-0 left-0 right-0 pointer-events-none"
                    style={{
                      background:
                        "linear-gradient(0deg, #FFF 0%, rgba(255, 255, 255, 0.00) 100%)",
                      height: "86px",
                    }}
                  />
                )}
              </div>
            </div>
          </DialogContent>
        </DialogPortal>
      </Dialog>
    </>
  );
};

// Helper functions for achievements
const getAchievementColor = (label: string): string => {
  const colors: { [key: string]: string } = {
    Winner: "#FF376C14", // red-100
    "Top Clicks": "#018AD414", // blue-100
    "Top Completion": "#029B6D14", // green-100
    "Top Hook": "#B132FF14", // purple-100
    "Top Engagement": "#2D62FC14", // blue-100
  };
  return colors[label] || "rgb(243 244 246)"; // gray-100 default
};

const getAchievementTextColor = (label: string): string => {
  const colors: { [key: string]: string } = {
    Winner: "#FF376C", // red-700
    "Top Clicks": "#018AD4", // blue-700
    "Top Completion": "#029B6D", // green-700
    "Top Hook": "#B132FF", // purple-700
    "Top Engagement": "#2D62FC", // blue-700
  };
  return colors[label] || "rgb(55 65 81)"; // gray-700 default
};

const getAchievementIcon = (label: string) => {
  const icons: { [key: string]: React.ReactNode } = {
    Winner: <IconTrophy size={14} />,
    "Top Clicks": <IconHandFinger size={14} />,
    "Top Completion": <IconMovie size={14} />,
    "Top Hook": <IconFishHook size={14} />,
    "Top Engagement": <IconPasswordUser size={14} />,
  };
  return icons[label] || <IconTarget size={14} />;
};

export default CreativeDetailsDialog;

const OverviewCard = ({
  title,
  titleIcon: TitleIcon,
  className,
  children,
  rightSideHeader,
}: {
  title: string;
  titleIcon: React.ComponentType<{ className?: string }>;
  className?: string;
  children: React.ReactNode;
  rightSideHeader?: React.ReactNode;
}) => {
  return (
    <div
      className={`flex flex-col gap-[10px] p-[12px] rounded-[16px] squircle squircle-radius-[16px] squircle-outline-color-border-gray squircle-outline-[1px] bg-white ${className}`}
    >
      {/* Header with title and optional info icon */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-[8px]">
          {TitleIcon && (
            <TitleIcon className="h-[14px] w-[14px] text-brand-primary-600" />
          )}
          <h3 className="text-[12px] font-normal text-brand-primary-800 translate-y-[1px]">
            {title}
          </h3>
        </div>
        {rightSideHeader}
      </div>

      {/* Content area */}
      <div className="">{children}</div>
    </div>
  );
};
